<template>
  <div class="applications-list-toolbar" v-if="phase && phase.name">
    <v-divider />
    <v-toolbar flat color="transparent">
      <v-toolbar-items>
        <v-menu
          offset-y
          :close-on-content-click="false"
          v-show="phase.name !== 'Withdrawn'"
          v-model="bulkActionsVisible"
        >
          <v-btn flat slot="activator">
            Bulk
            <v-icon right>mdi-chevron-down</v-icon>
          </v-btn>
          <v-card flat>
            <v-subheader>
              {{ selected.length }} {{ selected.length | pluralize('Application') }} selected
            </v-subheader>
            <v-list>
              <v-list-tile @click="selectAll" :disabled="allSelected">
                <v-list-tile-content>
                  <v-list-tile-title class="subheading">
                    Select All
                  </v-list-tile-title>
                </v-list-tile-content>
                <v-list-tile-action :style="allSelected ? 'opacity: 0.5;' : ''">
                  <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
                </v-list-tile-action>
              </v-list-tile>
              <v-list-tile @click="deselectAll" :disabled="!selected[0]">
                <v-list-tile-content>
                  <v-list-tile-title class="subheading">Deselect All</v-list-tile-title>
                </v-list-tile-content>
                <v-list-tile-action :style="!selected[0] ? 'opacity: 0.5;' : ''">
                  <v-icon>mdi-checkbox-multiple-blank-outline</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
            <v-divider />
            <ApplicationEventsList :applications="selected" include-regret @done="done" />
          </v-card>
        </v-menu>
      </v-toolbar-items>
      <v-spacer />
      <v-toolbar-items>
        <can I="ats" a="Opportunity">
          <v-tooltip bottom>
            <v-btn
              slot="activator"
              flat
              :disabled="!opportunity.downloadToken"
              :href="
                `${$root.$api}/admin/applications.xlsx?opportunity=${opportunity.id}&token=${
                  opportunity.downloadToken
                }`
              "
              target="_blank"
            >
              <span v-if="!iconsOnly">Export All</span>
              <v-icon :right="!iconsOnly" medium class="green--text text--darken-3">
                mdi-file-excel-box
              </v-icon>
            </v-btn>
            <span>Export all applications to Excel</span>
          </v-tooltip>
        </can>

        <can I="ats" a="Opportunity">
          <ApplicationsExportSelector :value="selected">
            <v-btn slot="activator" flat :disabled="!selected[0]">
              <span v-if="!iconsOnly">Export</span>
              <v-badge :value="!!selected[0]">
                <span slot="badge">{{ selected.length }}</span>
                <v-icon :right="!iconsOnly" medium class="red--text text--darken-3">
                  mdi-file-pdf-box
                </v-icon>
              </v-badge>
            </v-btn>
          </ApplicationsExportSelector>
        </can>

        <v-menu offset-y>
          <v-tooltip bottom slot="activator">
            <v-btn slot="activator" flat class="pr-1" style="min-width: 0px;">
              <span v-if="!iconsOnly">Sort by </span>
              <v-icon :right="!iconsOnly">{{ sortBy.icon }}</v-icon>
            </v-btn>
            <span>Sort applications</span>
          </v-tooltip>
          <v-list dark>
            <v-list-tile
              @click="changeSortBy(sort)"
              v-for="(sort, i) in sortValues"
              :key="`sort-${i}`"
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  <span class="caption">{{ sort.label }}</span>
                </v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-icon>{{ sort.icon }}</v-icon>
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
        </v-menu>

        <v-tooltip bottom slot="activator">
          <v-btn
            slot="activator"
            flat
            @click="changeSortDirection"
            class="pl-1"
            style="min-width: 0px;"
          >
            <v-icon medium v-if="sortBy.desc" key="sort-asc">mdi-sort-descending</v-icon>
            <v-icon medium v-else key="sort-desc">mdi-sort-ascending</v-icon>
          </v-btn>
          <span>Change sorting direction</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn slot="activator" flat @click="filtering = !filtering">
            <span v-if="!iconsOnly">Filter</span>
            <v-icon :right="!iconsOnly" medium>mdi-filter</v-icon>
          </v-btn>
          <span>Set application filters</span>
        </v-tooltip>
      </v-toolbar-items>
    </v-toolbar>
    <ApplicationsFilter v-model="filtering" />
    <v-divider />
  </div>
</template>

<script>
// Components
import ApplicationEventsList from '@/components/applications_tracker/applications/ApplicationEventsList'
import ApplicationsExportSelector from '@/components/applications_tracker/applications/ApplicationsExportSelector'
import ApplicationsFilter from '@/components/applications_tracker/applications/ApplicationsFilter'
import RenderlessApplicationEventsInput from '@/components/applications_tracker/applications/RenderlessApplicationEventsInput'

export default {
  props: { applications: Array, phase: Object },
  components: {
    ApplicationEventsList,
    ApplicationsExportSelector,
    ApplicationsFilter,
    RenderlessApplicationEventsInput,
  },
  data() {
    return {
      filtering: false,
      sortBy: {
        value: 'createdAt',
        label: 'Sort by Application Date',
        icon: 'mdi-calendar',
        desc: false,
      },
      sortValues: [
        {
          value: 'createdAt',
          label: 'Sort by Application Date',
          icon: 'mdi-calendar',
          desc: false,
        },
        {
          value: 'completion',
          label: 'Sort by Completion',
          icon: 'mdi-timelapse',
          desc: true,
        },
        {
          value: 'profile.lastName.sort',
          label: 'Sort by Name',
          icon: 'mdi-alphabetical',
          desc: false,
        },
        {
          value: 'applicationRating.value',
          label: 'Sort by Rating',
          icon: 'mdi-star',
          desc: true,
        },
        {
          value: 'profile.assessmentAggregate',
          label: 'Sort by Assessment',
          icon: 'mdi-account-search',
          desc: true,
        },
        {
          value: 'unreadChats',
          label: 'Unread Messages',
          icon: 'mdi-message',
          desc: true,
        },
      ],
      bulkActionsVisible: false,
    }
  },
  computed: {
    opportunity() {
      return this.$store.getters['admin/opportunities/find']()
    },
    selected() {
      return this.$store.getters['admin/applications/selected']
    },
    allSelected() {
      return (
        _.intersection(this.applications.map(a => a.id), this.selected).length ===
        this.applications.length
      )
    },
    iconsOnly() {
      return this.$vuetify.breakpoint.mdAndDown
    },
  },
  methods: {
    selectAll() {
      return this.$store.commit('admin/applications/select', this.applications.map(c => c.id))
    },
    deselectAll() {
      this.bulkActionsVisible = false
      return this.$store.commit('admin/applications/deselect', this.selected)
    },
    copiedToast(value) {
      this.$store.dispatch('snackbarShow', `Copied ${value}.`)
    },
    toggleProfileSection() {
      const sections = [
        'profile',
        'biography',
        'education',
        'experience',
        'skills',
        'achievements',
        'interests',
      ]
      const hasProfile = this.sections.find(s => sections.indexOf(s) >= 0)
      this.sections = this.sections.filter(s => sections.indexOf(s) < 0)
      if (!hasProfile) this.sections = [...this.sections, ...sections]
    },
    done(phase = {}) {
      this.deselectAll()
    },
    changeSortDirection() {
      this.sortBy.desc = !this.sortBy.desc
      this.$store.dispatch('admin/applications/updateSort', {
        by: this.sortBy.value,
        descending: this.sortBy.desc,
      })
    },
    changeSortBy(by) {
      this.sortBy = by
      this.sortBy.desc = false
      this.$store.dispatch('admin/applications/updateSort', { by: by.value, descending: false })
    },
  },
}
</script>
